import {
  Action,
  ActionType,
  AI_BUILDER_LAST_TEST_ERROR,
  AI_BUILDER_TEST_ERROR_MAX_LENGTH,
  AI_BUILDER_TESTED_INPUT,
  StepRunResponse,
  flowStructureUtil,
  isNil,
  stampTestedInput,
} from '@activepieces/shared';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { flowRunsApi } from '../../../features/flow-runs/lib/flow-runs-api';
import { useBuilderStateContext, useBuilderStore } from '../builder-hooks';

import { TestSampleDataViewer } from './test-sample-data-viewer';
import { TestButtonTooltip } from './test-step-tooltip';
import { testStepUtils } from './test-step-utils';

import { useSocket } from '@/components/socket-provider';
import { Button } from '@/components/ui/button';
import { Dot } from '@/components/ui/dot';
import { showErrorToast } from '@/components/ui/use-toast';
import { sampleDataApi } from '@/features/flows/lib/sample-data-api';

type TestActionComponentProps = {
  isSaving: boolean;
};

const TestActionSection = React.memo(
  ({ isSaving }: TestActionComponentProps) => {
    const [errorMessage, setErrorMessage] = useState<string | undefined>(
      undefined,
    );
    const form = useFormContext<Pick<Action, 'settings' | 'name'>>();
    const formValues = form.getValues();
    const [consoleLogs, setConsoleLogs] = useState<null | string>(null);
    const builderStore = useBuilderStore();
    const {
      sampleData,
      setSampleData,
      selectedStep,
      flowVersion,
      testTriggerFlag,
      resetTriggerTest,
      waitForSavesToSettle,
    } = useBuilderStateContext((state) => {
      return {
        sampleData: state.sampleData[formValues.name],
        setSampleData: state.setSampleData,
        selectedStep: state.selectedStep,
        flowVersion: state.flowVersion,
        testTriggerFlag: state.testTriggerFlag,
        resetTriggerTest: state.resetTriggerTest,
        waitForSavesToSettle: state.waitForSavesToSettle,
      };
    });
    const [isValid, setIsValid] = useState(false);

    useEffect(() => {
      setIsValid(form.formState.isValid);
    }, [form.formState.isValid]);

    const [lastTestDate, setLastTestDate] = useState(
      formValues.settings.inputUiInfo?.lastTestDate,
    );

    const sampleDataExists = !isNil(lastTestDate) || !isNil(errorMessage);

    const socket = useSocket();

    const { mutate, isPending: isTesting } = useMutation<
      StepRunResponse & { sampleDataFileId?: string },
      Error,
      void
    >({
      mutationFn: async () => {
        await waitForSavesToSettle();
        const testedFlowVersionId = builderStore.getState().flowVersion.id;
        const testStepResponse = await flowRunsApi.testStep(socket, {
          flowVersionId: testedFlowVersionId,
          stepName: formValues.name,
        });
        let sampleDataFileId: string | undefined = undefined;
        if (testStepResponse.success && !isNil(testStepResponse.output)) {
          const sampleFile = await sampleDataApi.save({
            flowVersionId: testedFlowVersionId,
            stepName: formValues.name,
            payload: testStepResponse.output,
          });
          sampleDataFileId = sampleFile.id;
        }
        return {
          ...testStepResponse,
          sampleDataFileId,
        };
      },
      onSuccess: ({
        success,
        output,
        sampleDataFileId,
        standardOutput,
        standardError,
      }) => {
        if (success) {
          setErrorMessage(undefined);

          const testedAt = dayjs().toISOString();
          const newInputUiInfo = {
            ...formValues.settings.inputUiInfo,
            sampleDataFileId,
            currentSelectedData: undefined,
            lastTestDate: testedAt,
            [AI_BUILDER_LAST_TEST_ERROR]: undefined,
            [AI_BUILDER_TESTED_INPUT]: stampTestedInput(
              'input' in formValues.settings
                ? formValues.settings.input
                : undefined,
              testedAt,
              'test',
            ),
          };
          form.setValue(
            'settings.inputUiInfo',
            newInputUiInfo as typeof formValues.settings.inputUiInfo,
            {
              shouldValidate: true,
            },
          );
        } else {
          const formattedError = testStepUtils.formatErrorMessage(
            JSON.stringify(output) ||
              t('Failed to run test step and no error message was returned'),
          );
          setErrorMessage(formattedError);
          // A failed test persists no sample data: without this the AI Builder
          // only sees an untested step and asks for the test again. The
          // MESSAGE only — the raw output carries the request headers
          form.setValue(
            'settings.inputUiInfo',
            {
              ...formValues.settings.inputUiInfo,
              [AI_BUILDER_LAST_TEST_ERROR]: formattedError.slice(
                0,
                AI_BUILDER_TEST_ERROR_MAX_LENGTH,
              ),
            } as typeof formValues.settings.inputUiInfo,
            {
              shouldValidate: true,
            },
          );
        }
        setSampleData(formValues.name, output);
        setConsoleLogs(standardOutput || standardError);
        setLastTestDate(dayjs().toISOString());
      },
      onError: (error) => {
        console.error(error);
        showErrorToast(error);
      },
    });

    useEffect(() => {
      if (testTriggerFlag && !isTesting) {
        mutate();
        resetTriggerTest();
      }
    }, [testTriggerFlag, isTesting, mutate, resetTriggerTest]);

    return (
      <>
        {!sampleDataExists && (
          <div className="flex-grow flex justify-center items-center w-full h-full">
            <TestButtonTooltip disabled={!isValid}>
              <Button
                variant="outline"
                size="sm"
                onClick={() => mutate()}
                keyboardShortcut="G"
                onKeyboardShortcut={mutate}
                loading={isTesting}
                disabled={!isValid}
              >
                <Dot animation={true} variant={'primary'}></Dot>
                {t('Test Step')}
              </Button>
            </TestButtonTooltip>
          </div>
        )}
        {sampleDataExists && (
          <TestSampleDataViewer
            onRetest={mutate}
            isValid={isValid}
            isSaving={isSaving}
            isTesting={isTesting}
            sampleData={sampleData}
            errorMessage={errorMessage}
            lastTestDate={lastTestDate}
            consoleLogs={
              selectedStep &&
              flowStructureUtil.getStep(selectedStep, flowVersion)?.type ===
                ActionType.CODE
                ? consoleLogs
                : null
            }
          ></TestSampleDataViewer>
        )}
      </>
    );
  },
);
TestActionSection.displayName = 'TestActionSection';

export { TestActionSection };
